import { useRegisterSW } from 'virtual:pwa-register/react';
import { Button, Snackbar } from '@mui/material';
import { useTranslation } from 'react-i18next';

/**
 * How often an app that stays open asks the server for a newer build.
 *
 * The browser only looks for a new service worker on a navigation, and an
 * installed PWA left open on a phone may not navigate for days: on 2026-10-01
 * an operator kept sending 1 h opens from a bundle a deploy had replaced. With
 * this, the «new version» bar appears within a day without a manual reload.
 */
const UPDATE_CHECK_MS = 24 * 60 * 60 * 1000;

export default function PWAUpdatePrompt() {
  const { t } = useTranslation('common');
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return;
      setInterval(() => {
        // Offline (a field with no coverage) the check would just fail.
        if (registration.installing || !navigator.onLine) return;
        registration.update().catch(() => { /* next tick retries */ });
      }, UPDATE_CHECK_MS);
    },
  });

  const handleClose = () => setNeedRefresh(false);

  return (
    <Snackbar
      open={needRefresh}
      message={t('pwa.updateAvailable', 'A new version is available')}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      action={
        <>
          <Button color="secondary" size="small" onClick={handleClose}>
            {t('pwa.later', 'Later')}
          </Button>
          <Button color="primary" size="small" onClick={() => updateServiceWorker(true)}>
            {t('pwa.update', 'Update')}
          </Button>
        </>
      }
    />
  );
}
